import * as React from 'react';
import { connect } from 'react-redux';
import { actions } from 'react-redux-form';
import { find, sortBy, debounce } from 'lodash';
import * as queryString from 'query-string';
import * as moment from 'moment';
import * as validator from 'validator';
import {
  Button,
  Card,
  Form,
  Icon,
  IconButton,
  Notification,
  Tooltip,
  CompanyList,
  DriverList,
} from '..';
import {
  addBooking,
  showError,
  clearErrors,
  getBookingCollectionTimes,
} from '../../redux/actions/bookings';
import { getDrivers } from '../../redux/actions/drivers';
import { searchCompanies } from '../../redux/actions/companies';
import FIELDS from '../../data/fields';
import Loader from '../Loader/Loader';

interface Props {
  onUpdate: Function;
  vehicleTypes: Array<any>;
  dispatch: Function;
  isFetching: boolean;
  didInvalidate: boolean;
  error: string | boolean;
}

interface State {
  data: any;
  times: Array<any>;
  startDate: any;
  isLoadingTimes: boolean;
  isLoading: boolean;
  changeField: Function;
  booking?: any;
  section: 0 | 1;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  drivers: Array<any>;
  companies: Array<any>;
  isLoadingContacts: boolean;
  assignedContact: any;
  isSubmitting: boolean;
  duration: number;
  isCompany: boolean;
  hasBadDriver: boolean;
}

/**
 * Form for booking creation
 */
class BookingCreateForm extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      data: {},
      times: [],
      startDate: undefined,
      isLoadingTimes: true,
      isLoading: true,
      changeField: () => {},
      section: 0,
      drivers: [],
      companies: [],
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      isLoadingContacts: false,
      assignedContact: false,
      isSubmitting: false,
      duration: 0,
      isCompany: false,
      hasBadDriver: false,
    };
    this.handleValidate = this.handleValidate.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleContactSearch = debounce(this.handleContactSearch, 300);
    this.setContact = this.setContact.bind(this);
    this.renderForm = this.renderForm.bind(this);
    this.renderContact = this.renderContact.bind(this);
  }

  /**
   * Load vehicle types from the API
   */
  async componentDidMount() {
    this.props.dispatch(clearErrors());
    this.setState({ isLoading: false });
  }

  /**
   * Handle field validation
   * @param {Object} data
   * @param {Function} isIndex?
   * @return {Object|Boolean}
   */
  handleValidate(data, isIndex = (index: number) => true): any {
    if (this.props.didInvalidate) {
      this.props.dispatch(clearErrors());
    }

    const throwError = (error) => {
      this.props.dispatch(showError(error));
      return false;
    };

    const start = moment(`${data.start_date} ${data.start_time}:00`);
    const end = moment(`${data.end_date} 23:59:59`);

    // Start date
    if (isIndex(0)) {
      if (validator.isEmpty(data.start_date)) {
        return throwError('Please specify a start date');
      }
    }

    // Start time
    if (isIndex(1)) {
      if (!find(this.state.times, { value: data.start_time })) {
        return throwError('Please specify a collection time');
      }
      if (!start.isValid()) {
        throwError('The start date is not in a valid format');
      }
    }

    // End date
    if (isIndex(2)) {
      if (validator.isEmpty(data.end_date)) {
        return throwError('Please specify an end date');
      }
      if (!end.isValid()) {
        return throwError('The end date is not in a valid format');
      }
      if (!end.isSameOrAfter(start)) {
        return throwError('Make sure the booking end date is after the start');
      }
    }

    // Vehicle type
    if (isIndex(3)) {
      if (!validator.isNumeric(String(data.vehicle_type_id))) {
        return throwError('Please specify a vehicle type');
      }
    }

    return {
      start: start.format('YYYY-MM-DD HH:mm:ss'),
      end: end.format('YYYY-MM-DD HH:mm:ss'),
      vehicle_type_id: Number(data.vehicle_type_id),
    };
  }

  /**
   * Handle form submission
   */
  async handleSubmit() {
    if (this.state.isSubmitting) {
      return;
    }
    this.setState({ isSubmitting: true });
    const data = this.state.data;

    // Create a new contact if one hasn't been chosen
    if (this.state.assignedContact === false) {
      data.contact_name = this.state.contactName ? this.state.contactName : '';
      data.contact_email = this.state.contactEmail
        ? this.state.contactEmail
        : '';
      data.contact_phone_number = this.state.contactPhone
        ? this.state.contactPhone
        : '';
    } else if (this.state.isCompany) {
      data.company_id = this.state.assignedContact.id;
    } else {
      data.driver_id = this.state.assignedContact.id;
    }

    const response = await this.props.dispatch(addBooking(this.state.data));
    this.setState({ booking: response.data, isSubmitting: false });
    this.props.onUpdate(response.data);
  }

  /**
   * Handle searching for a contact
   * @param {String} contactName
   */
  handleContactSearch(contactName) {
    if (validator.isEmpty(contactName)) {
      this.setState({ drivers: [], companies: [], contactName: '' });
    } else if (contactName !== this.state.contactName) {
      this.setState(
        { contactName, isLoadingContacts: true, hasBadDriver: false },
        async () => {
          const drivers = await this.props.dispatch(
            getDrivers(undefined, 0, contactName),
          );
          const companies = await this.props.dispatch(
            searchCompanies(contactName),
          );
          this.setState({
            drivers: drivers.data,
            companies: companies.data,
            isLoadingContacts: false,
          });
        },
      );
    }
  }

  /**
   * Set the booking's contact to be an existing resource
   * @param {Object} contact
   */
  setContact(assignedContact, contactType: 'company' | 'driver') {
    this.setState({ assignedContact, isCompany: contactType === 'company' });
  }

  /**
   * Render the first part of the form, the field input
   */
  renderForm() {
    return (
      <Card>
        <div className="field">
          <Form
            fields={FIELDS.bookings.create}
            values={{
              start_time: this.state.times,
              vehicle_type_id: this.props.vehicleTypes,
            }}
            type="create"
            buttonText="Next"
            disabled={this.props.isFetching}
            handleSubmit={async (data) => {
              if (!this.state.isLoadingTimes) {
                const validatedData = this.handleValidate(data);
                if (validatedData !== false) {
                  this.setState({ data: validatedData, section: 1 });
                }
              }
            }}
            handleChange={(data) => {
              // Handle dynamic collection times
              const startDate = data.start_date;
              if (startDate && startDate !== this.state.startDate) {
                this.setState({ isLoadingTimes: true }, async () => {
                  const times = await this.props.dispatch(
                    getBookingCollectionTimes(startDate),
                  );
                  if (!times.error) {
                    this.setState({
                      startDate,
                      times: times.data.map((time) => ({
                        name: time,
                        value: time,
                      })),
                      isLoadingTimes: false,
                    });
                  }
                });
              }

              // Bump the end date if it's below the start date
              const start = moment(`${data.start_date} ${data.start_time}:00`);
              const end = moment(`${data.end_date} 23:59:59`);
              if (
                start.isValid() &&
                ((end.isValid() && !end.isSameOrAfter(start)) || !end.isValid())
              ) {
                this.state.changeField('create.end_date', data.start_date);
              }

              this.setState({
                duration: 1 + end.diff(start, 'days'),
              });
            }}
            // Populate certain fields if passed via URL param
            getDispatch={(dispatch) => {
              const query = queryString.parse(window.location.search);
              dispatch(
                actions.change(
                  'create.start_date',
                  query && query.start_date ? query.start_date : '',
                ),
              );
              dispatch(actions.change('create.start_time', ''));
              dispatch(
                actions.change(
                  'create.end_date',
                  query && query.end_date ? query.end_date : '',
                ),
              );
              dispatch(
                actions.change(
                  'create.vehicle_type_id',
                  query && query.vehicle_type_id ? query.vehicle_type_id : '',
                ),
              );
              this.setState({
                changeField: (field, value) => {
                  dispatch(actions.change(field, value));
                },
              });
            }}
          />
        </div>
        {this.state.duration > 0 && (
          <Notification type="default">
            This booking will be for {this.state.duration} day
            {this.state.duration === 1 ? '' : 's'}
          </Notification>
        )}
      </Card>
    );
  }

  /**
   * Render the second part of the form, the contact assigning procedure
   */
  renderContact() {
    return (
      <Card list>
        <h4 className="heading">Contact Information</h4>
        {/* Name/phone number specification */}
        {this.state.assignedContact === false && (
          <div className="field">
            <Form
              fields={[
                { name: 'name', type: 'text', label: 'Name' },
                { name: 'phone_number', type: 'text', label: 'Phone Number' },
                { name: 'email', type: 'text', label: 'Email' },
              ]}
              type="contact"
              hasButton={false}
              handleChange={(data) => {
                this.setState({
                  contactPhone: data.phone_number,
                  contactEmail: data.email,
                });
                this.handleContactSearch(data.name);
              }}
              getDispatch={(dispatch) => {
                dispatch(
                  actions.change(
                    'contact.name',
                    this.state.data.name || this.state.contactName || '',
                  ),
                );
                dispatch(
                  actions.change(
                    'contact.phone_number',
                    this.state.data.phone_number ||
                      this.state.contactPhone ||
                      '',
                  ),
                );
                dispatch(
                  actions.change(
                    'contact.email',
                    this.state.data.email || this.state.contactEmail || '',
                  ),
                );
              }}
            />
          </div>
        )}
        {this.state.assignedContact !== false && (
          <div className="field is-clearfix">
            <Button
              className="is-danger is-outlined is-pulled-right"
              handleClick={() => {
                const data = this.state.data;
                delete data.company_id;
                delete data.driver_id;
                this.setState({
                  data,
                  assignedContact: false,
                  contactName: '',
                  contactPhone: '',
                  contactEmail: '',
                });
              }}
            >
              Unset Contact
            </Button>
            <label>Name</label>
            <p>{this.state.assignedContact.name}</p>
            <label>Phone Number</label>
            <p>{this.state.assignedContact.phone_number}</p>
            <label>Email</label>
            <p>
              {this.state.assignedContact.email ||
                this.state.assignedContact.general_contact_email}
            </p>
          </div>
        )}

        {/* Helpers */}
        {this.state.contactName === '' && !this.state.isLoadingContacts && (
          <Notification type="default">
            <Icon icon="question-circle" />
            &nbsp; Start typing out the contact's name to begin searching for
            existing customers
          </Notification>
        )}

        <div className="field">
          <IconButton
            icon="arrow-left"
            className="is-default"
            handleClick={() => this.setState({ section: 0 })}
          >
            Back
          </IconButton>

          {/* Submit button */}
          {this.state.contactName !== '' &&
            !this.state.isSubmitting &&
            !this.state.isLoadingContacts && (
              <div className="is-pulled-right">
                <Button className="is-primary " handleClick={this.handleSubmit}>
                  Create Booking
                </Button>
              </div>
            )}
        </div>

        {/* Results */}
        {this.state.contactName !== '' &&
          (this.state.drivers &&
            (this.state.drivers.length > 0 || this.state.companies.length > 0)
          ) &&
          !this.state.isLoadingContacts &&
          this.state.assignedContact === false && (
            <div className="field">
              <p>
                <strong>Existing Contacts</strong>
                <Tooltip text="Choose one of these to re-use contact information">
                  <Icon icon="question-circle" />
                </Tooltip>
              </p>
              {this.state.drivers.length > 0 && (
                <DriverList
                  hideSearch
                  items={this.state.drivers}
                  showJoinDate={false}
                  showReputation={true}
                  handleClick={(driver) => {
                    if (driver.reputation === 2) {
                      this.setState({ hasBadDriver: true });
                    } else {
                      this.setContact(driver, 'driver');
                    }
                  }}
                />
              )}
              {this.state.companies && this.state.companies.length > 0 && (
                <CompanyList
                  small
                  hideSearch
                  items={this.state.companies}
                  showJoinDate={false}
                  handleClick={(company) => {
                    this.setContact(company, 'company');
                  }}
                />
              )}
            </div>
          )}

        {/* Loader */}
        {this.state.isLoadingContacts && <Loader fullwidth />}
      </Card>
    );
  }

  private renderSectionTabs = () => (
    <div className="buttons">
      {['Details', 'Contact'].map((name, index: 0 | 1) => (
        <button
          className={`button ${
            this.state.section === index
              ? 'is-primary'
              : this.state.section > index
              ? 'is-default'
              : 'is-static'
          }`}
          onClick={() => {
            if (this.state.section > index) {
              this.setState({ section: index });
            }
          }}
        >
          {name}
        </button>
      ))}
    </div>
  );

  render() {
    if (this.state.isLoading) {
      return <Loader fullwidth />;
    }
    return (
      <div>
        <div className="field">
          {this.renderSectionTabs()}
          {this.state.section === 0 && this.renderForm()}
          {this.state.section === 1 && this.renderContact()}
        </div>
        <div className="field">
          {!this.props.isFetching && this.props.didInvalidate && (
            <Notification type="danger">{this.props.error}</Notification>
          )}
          {this.state.hasBadDriver && !this.state.isLoading && (
            <Notification type="danger">
              Cannot create a booking for a driver with a bad reputation.
            </Notification>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ bookings, vehicleTypes, companies }) => ({
  isFetching: bookings.isFetching,
  didInvalidate: bookings.didInvalidate,
  error: bookings.error,
  vehicleTypes: sortBy(vehicleTypes.vehicleTypes, ['size', 'id']).map(
    (vehicleType) => ({
      name: vehicleType.name,
      value: vehicleType.id,
    }),
  ),
  companies: sortBy(companies.companies, 'name').map((company) => ({
    name: company.name,
    value: company.id,
  })),
});

export default connect(mapStateToProps)(BookingCreateForm);
